// src/store/slices/subscriptionSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/modules/shared/api/axios";
import { normalizePaymentMethods } from "@/modules/subscriptions/utils/paymentMethods";

const normalizeError = (err) =>
  err?.response?.data || err?.message || "Unexpected error";

const IMPORTING_ENTITY_MODULE_KEY = "Importing_Entity_Plan";

const withImportingEntityModule = (modules, enabled) => {
  const normalized = Array.isArray(modules) ? [...modules] : [];
  if (!enabled || normalized.includes(IMPORTING_ENTITY_MODULE_KEY)) {
    return normalized;
  }

  normalized.push(IMPORTING_ENTITY_MODULE_KEY);
  return normalized;
};

const payloadHasImportingEntityPlan = (payload = {}) => {
  if (payload?.importing_entity_plan) {
    return true;
  }

  const activeSubscriptions = Array.isArray(payload?.active_subscriptions)
    ? payload.active_subscriptions
    : [];

  return activeSubscriptions.some(
    (subscription) => subscription?.importing_entity_plan === true
  );
};

// Validate promo code for a plan/duration
export const validatePromoCode = createAsyncThunk(
  "subscriptions/validatePromoCode",
  async ({ promo_code, plan_id, duration_id }, { rejectWithValue }) => {
    try {
      const res = await api.post("/promo/validate", {
        promo_code,
        plan_id,
        duration_id,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Fetch module pricing (module-based subscriptions)
export const fetchModulePricing = createAsyncThunk(
  "subscriptions/fetchModulePricing",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/module-pricing");
      const data = res.data || {};
      return {
        ...data,
        freeModules: data.free_modules ?? data.freeModules ?? [],
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Fetch all subscription plans (sorted by display_number)
export const fetchSubscriptions = createAsyncThunk(
  "subscriptions/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/plans");
      const rawPlans = res.data?.plans || [];
      const companyType = String(res.data?.company_type || "")
        .toLowerCase()
        .replace(/[-\s]+/g, "_") || null;
      const paymentMethods = normalizePaymentMethods(
        res.data?.payment_methods,
        companyType === "non_saudi"
          ? ["CARD", "APPLE_PAY"]
          : ["CARD", "BANK_TRANSFER", "APPLE_PAY", "SADAD"]
      );
      const plans = rawPlans.map((p) => ({
        id: p.plan_id || p.id,
        plan_id: p.plan_id || p.id,
        plan_name_en: p.plan_name_en,
        plan_name_ar: p.plan_name_ar,
        plan_name_zh_hans: p.plan_name_zh_hans,
        isPopular: p.is_popular || p.isPopular || false,
        company_type: String(p.company_type || p.companyType || "both")
          .toLowerCase()
          .replace(/[-\s]+/g, "_"),
        applicable_entity_types: Array.isArray(p.applicable_entity_types)
          ? p.applicable_entity_types
          : ["tendering_entity", "supplier_entity"],
        // Backend /plans already filters Active plans, but some responses
        // don't include an `active` boolean. Treat missing as active.
        active: p.active === undefined ? true : !!p.active,
        full_access: !!p.full_access,
        importing_entity_plan: !!p.importing_entity_plan,
        allowed_modules: withImportingEntityModule(
          Array.isArray(p.allowed_modules) ? p.allowed_modules : [],
          !!p.importing_entity_plan
        ),
        plan_features: Array.isArray(p.plan_features) ? p.plan_features : [],
        durations: Array.isArray(p.durations) ? p.durations : [],
      }));
      return { plans, companyType, paymentMethods };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Check if user/company already subscribed
export const checkSubscription = createAsyncThunk(
  "subscriptions/check",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/subscription/status", { forceRefresh: true });
      return res.data || {};
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Prevents spamming /subscription/status across route changes.
export const ensureSubscriptionStatus = createAsyncThunk(
  "subscriptions/ensureStatus",
  async (_, { dispatch, getState }) => {
    const state = getState()?.subscriptions || {};
    const now = Date.now();
    const cooldownMs = 30_000;

    if (
      typeof state.lastCheckAttemptAt === "number" &&
      now - state.lastCheckAttemptAt < 3_000
    ) {
      return { skipped: true };
    }

    if (
      state.statusLoaded === true &&
      typeof state.lastCheckedAt === "number" &&
      now - state.lastCheckedAt < cooldownMs
    ) {
      return { skipped: true };
    }

    return dispatch(checkSubscription()).unwrap();
  }
);

// Subscribe when price is 0 (free)
export const subscribeFree = createAsyncThunk(
  "subscriptions/subscribeFree",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = {
        plan_id,
        duration_id,
        payment_reference: promo_code ? `PROMO_${promo_code}` : "FREE",
        amount_paid: 0,
      };
      if (promo_code) {
        payload.promo_code = promo_code;
      }
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }
      const res = await api.post("/subscribe", payload);
      await dispatch(checkSubscription());
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const upgradeSubscribeFree = createAsyncThunk(
  "subscriptions/upgradeSubscribeFree",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = {
        plan_id,
        duration_id,
        payment_reference: promo_code ? `UPGRADE_PROMO_${promo_code}` : "UPGRADE_FREE",
        amount_paid: 0,
        source: "upgrade",
        payment_method: "Free",
      };
      if (promo_code) {
        payload.promo_code = promo_code;
      }
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }
      const res = await api.post("/upgrade/subscribe", payload);
      await dispatch(checkSubscription());
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const subscribeModulesFree = createAsyncThunk(
  "subscriptions/subscribeModulesFree",
  async ({ module_ids, duration_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/module-subscriptions/free", {
        module_ids,
        duration_id,
      });
      await dispatch(checkSubscription());
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Paid plans: initialize HyperPay checkout session
export const createCheckoutSession = createAsyncThunk(
  "subscriptions/createCheckoutSession",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        plan_id,
        duration_id,
      };

      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/pay/checkout", payload);
      return {
        ...(res.data || {}),
        plan_id,
        duration_id,
        promo_code: (res.data && res.data.promoCode) || payload.promo_code,
        discount_amount:
          (res.data && res.data.discountAmount) ?? payload.discount_amount,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const createUpgradeCheckoutSession = createAsyncThunk(
  "subscriptions/createUpgradeCheckoutSession",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        plan_id,
        duration_id,
      };

      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/upgrade/checkout", payload);
      return {
        ...(res.data || {}),
        plan_id,
        duration_id,
        promo_code: (res.data && res.data.promoCode) || payload.promo_code,
        discount_amount:
          (res.data && res.data.discountAmount) ?? payload.discount_amount,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const createApplePayCheckoutSession = createAsyncThunk(
  "subscriptions/createApplePayCheckoutSession",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {
      const payload = { plan_id, duration_id };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/pay/apple/checkout", payload);
      return {
        ...(res.data || {}),
        plan_id,
        duration_id,
        promo_code: (res.data && res.data.promoCode) || payload.promo_code,
        discount_amount:
          (res.data && res.data.discountAmount) ?? payload.discount_amount,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const createUpgradeApplePayCheckoutSession = createAsyncThunk(
  "subscriptions/createUpgradeApplePayCheckoutSession",
  async (
    { plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {
      const payload = { plan_id, duration_id };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/upgrade/apple/checkout", payload);
      return {
        ...(res.data || {}),
        plan_id,
        duration_id,
        promo_code: (res.data && res.data.promoCode) || payload.promo_code,
        discount_amount:
          (res.data && res.data.discountAmount) ?? payload.discount_amount,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const createModuleCheckoutSession = createAsyncThunk(
  "subscriptions/createModuleCheckoutSession",
  async ({ module_ids, duration_id }, { rejectWithValue }) => {
    try {
      const res = await api.post("/pay/modules/checkout", {
        module_ids,
        duration_id,
      });
      return {
        ...(res.data || {}),
        module_ids,
        duration_id,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Paid plans: bank transfer flow (upload slip, wait for approval)
export const bankTransferSubscribe = createAsyncThunk(
  "subscriptions/bankTransferSubscribe",
  async (
    { plan_id, duration_id, payment_slip, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {

      const formData = new FormData();
      formData.append("plan_id", plan_id);
      formData.append("duration_id", duration_id);
      if (payment_slip) {
        formData.append("payment_slip", payment_slip);
      }
      if (promo_code) {
        formData.append("promo_code", promo_code);
      }
      if (typeof discount_amount === "number") {
        formData.append("discount_amount", String(discount_amount));
      }

      const res = await api.post("/subscribe/bank-transfer", formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const upgradeBankTransferSubscribe = createAsyncThunk(
  "subscriptions/upgradeBankTransferSubscribe",
  async (
    { plan_id, duration_id, payment_slip, promo_code, discount_amount },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append("plan_id", plan_id);
      formData.append("duration_id", duration_id);
      if (payment_slip) {
        formData.append("payment_slip", payment_slip);
      }
      if (promo_code) {
        formData.append("promo_code", promo_code);
      }
      if (typeof discount_amount === "number") {
        formData.append("discount_amount", String(discount_amount));
      }

      const res = await api.post("/upgrade/bank-transfer", formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const moduleBankTransferSubscribe = createAsyncThunk(
  "subscriptions/moduleBankTransferSubscribe",
  async ({ module_ids, duration_id, payment_slip }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      module_ids.forEach((id) => formData.append("module_ids[]", id));
      formData.append("duration_id", duration_id);
      if (payment_slip) {
        formData.append("payment_slip", payment_slip);
      }
      const res = await api.post("/module-subscriptions/bank-transfer", formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const submitVatStep = createAsyncThunk(
  "subscriptions/submitVatStep",
  async ({ choice, vat_number, subscription_id }, { rejectWithValue, dispatch }) => {
    try {
      const payload = {
        choice,
        vat_number,
        subscription_id,
      };
      const res = await api.post("/subscription/vat", payload);
      await dispatch(checkSubscription());
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const submitDeclarationStep = createAsyncThunk(
  "subscriptions/submitDeclarationStep",
  async ({ declaration, subscription_id }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      if (subscription_id) {
        formData.append("subscription_id", subscription_id);
      }
      formData.append("declaration", declaration);

      const res = await api.post("/subscription/declaration", formData);
      await dispatch(checkSubscription());
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

// Paid plans: verify HyperPay result + trigger subscription creation
export const verifyPaymentStatus = createAsyncThunk(
  "subscriptions/verifyPaymentStatus",
  async (
    { resourcePath, plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = {
        resourcePath,
        plan_id,
        duration_id,
      };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/pay/payment-status", payload);
      if (res?.data?.status === "success") {
        await dispatch(checkSubscription());
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const verifyUpgradePaymentStatus = createAsyncThunk(
  "subscriptions/verifyUpgradePaymentStatus",
  async (
    { resourcePath, plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = {
        resourcePath,
        plan_id,
        duration_id,
      };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/upgrade/payment-status", payload);
      if (res?.data?.status === "success") {
        await dispatch(checkSubscription());
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const verifyApplePayStatus = createAsyncThunk(
  "subscriptions/verifyApplePayStatus",
  async (
    { resourcePath, plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = { resourcePath, plan_id, duration_id };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/pay/apple/payment-status", payload);
      if (res?.data?.status === "success") {
        await dispatch(checkSubscription());
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const verifyUpgradeApplePayStatus = createAsyncThunk(
  "subscriptions/verifyUpgradeApplePayStatus",
  async (
    { resourcePath, plan_id, duration_id, promo_code, discount_amount },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = { resourcePath, plan_id, duration_id };
      if (promo_code) payload.promo_code = promo_code;
      if (typeof discount_amount === "number") {
        payload.discount_amount = discount_amount;
      }

      const res = await api.post("/upgrade/apple/payment-status", payload);
      if (res?.data?.status === "success") {
        await dispatch(checkSubscription());
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

export const verifyModulePaymentStatus = createAsyncThunk(
  "subscriptions/verifyModulePaymentStatus",
  async ({ resourcePath, payment_transaction_uuid }, { rejectWithValue, dispatch }) => {
    try {
      const payload = {
        resourcePath,
        payment_transaction_uuid,
      };
      const res = await api.post("/pay/modules/payment-status", payload);
      if (res?.data?.status === "success") {
        await dispatch(checkSubscription());
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeError(err));
    }
  }
);

const initialState = {
  plans: [],
  companyType: null,
  paymentMethods: [],
  modulePricing: {
    modules: [],
    durations: [],
    freeModules: [],
  },
  modulePricingLoading: false,
  modulePricingError: null,
  loading: false,
  subscribed: false,
  statusLoaded: false,
  statusError: null,
  statusRequestId: null,
  lastCheckedAt: null,
  lastCheckAttemptAt: null,
  statusChecking: false,
  currentPlanId: null,
  currentDurationId: null,
  currentPlanName: null,
  currentPlanNameZhHans: null,
  currentDurationLabel: null,
  currentStatus: null,
  currentPaymentMethod: null,
  currentPaymentVerificationStatus: null,
  currentPaymentReference: null,
  currentStartDate: null,
  currentEndDate: null,
  currentSubscriptionId: null,
  allowedModules: [],
  activeSubscriptions: [],
  legacyFullAccess: false,
  flowState: null,
  vatChoice: null,
  vatStatus: null,
  vatNumber: null,
  declarationStatus: null,
  declarationFile: null,
  declarationFileUrl: null,
  error: null,
  checkoutSession: null,
  paymentStatus: null,
  paymentLoading: false,
  paymentError: null,
  promoLoading: false,
  promoData: null,
  promoError: null,
  bankTransferLoading: false,
  bankTransferError: null,
  bankTransferData: null,
  vatSubmitting: false,
  vatError: null,
  declarationSubmitting: false,
  declarationError: null,
};

const subscriptionSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {
    // Used by global interceptors when backend indicates subscription
    // is no longer valid (e.g. 403). This avoids extra status checks
    // while ensuring the UI redirects to /subscribe.
    forceUnsubscribed: (state) => {
      state.subscribed = false;
      state.currentPlanId = null;
      state.currentDurationId = null;
      state.currentPlanName = null;
      state.currentPlanNameZhHans = null;
      state.currentDurationLabel = null;
      state.currentStatus = null;
      state.currentPaymentMethod = null;
      state.currentPaymentVerificationStatus = null;
      state.currentPaymentReference = null;
      state.currentStartDate = null;
      state.currentEndDate = null;
      state.allowedModules = [];
      state.activeSubscriptions = [];
      state.legacyFullAccess = false;
      state.statusLoaded = true;
      state.statusError = null;
      state.lastCheckedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch plans
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = Array.isArray(action.payload?.plans)
          ? action.payload.plans
          : [];
        state.companyType = action.payload?.companyType || null;
        state.paymentMethods = Array.isArray(action.payload?.paymentMethods)
          ? action.payload.paymentMethods
          : [];
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch module pricing
      .addCase(fetchModulePricing.pending, (state) => {
        state.modulePricingLoading = true;
        state.modulePricingError = null;
      })
      .addCase(fetchModulePricing.fulfilled, (state, action) => {
        state.modulePricingLoading = false;
        state.modulePricing = {
          modules: Array.isArray(action.payload?.modules)
            ? action.payload.modules
            : [],
          durations: Array.isArray(action.payload?.durations)
            ? action.payload.durations
            : [],
          freeModules: Array.isArray(action.payload?.free_modules)
            ? action.payload.free_modules
            : [],
        };
      })
      .addCase(fetchModulePricing.rejected, (state, action) => {
        state.modulePricingLoading = false;
        state.modulePricingError = action.payload;
      })
      // Validate promo
      .addCase(validatePromoCode.pending, (state) => {
        state.promoLoading = true;
        state.promoError = null;
        state.promoData = null;
      })
      .addCase(validatePromoCode.fulfilled, (state, action) => {
        state.promoLoading = false;
        state.promoData = action.payload;
      })
      .addCase(validatePromoCode.rejected, (state, action) => {
        state.promoLoading = false;
        state.promoError = action.payload;
        state.promoData = null;
      })
      // Check subscription
      .addCase(checkSubscription.pending, (state, action) => {
        state.statusChecking = true;
        state.statusError = null;
        state.lastCheckAttemptAt = Date.now();
        state.statusRequestId = action.meta.requestId;
      })
      .addCase(checkSubscription.fulfilled, (state, action) => {
        if (state.statusRequestId && state.statusRequestId !== action.meta.requestId) {
          return;
        }
        const payload = action.payload || {};
        const importingEntityEnabled = payloadHasImportingEntityPlan(payload);
        state.statusChecking = false;
        state.subscribed = !!payload.subscribed;
        state.currentPlanId = payload.plan_id || null;
        state.currentDurationId = payload.duration_id || null;
        state.currentPlanName = payload.plan_name || null;
        state.currentPlanNameZhHans = payload.plan_name_zh_hans || null;
        state.currentDurationLabel = payload.duration || null;
        state.currentStatus = payload.status || null;
        state.currentPaymentMethod = payload.payment_method || null;
        state.currentPaymentVerificationStatus =
          payload.payment_verification_status || null;
        state.currentPaymentReference = payload.payment_reference || null;
        state.currentStartDate = payload.start_date || null;
        state.currentEndDate = payload.end_date || null;
        state.currentSubscriptionId = payload.local_id || null;
        state.allowedModules = withImportingEntityModule(
          Array.isArray(payload.allowed_modules) ? payload.allowed_modules : [],
          importingEntityEnabled
        );
        state.activeSubscriptions = Array.isArray(payload.active_subscriptions)
          ? payload.active_subscriptions
          : [];
        state.legacyFullAccess = !!payload.legacy_full_access;
        state.flowState = payload.flow_state || null;
        state.vatChoice = payload.vat_choice || null;
        state.vatStatus = payload.vat_status || null;
        state.vatNumber = payload.vat_number || null;
        state.declarationStatus = payload.declaration_status || null;
        state.declarationFile = payload.declaration_file_meta || null;
        state.declarationFileUrl = payload.declaration_file || null;
        state.statusLoaded = true;
        state.statusError = null;
        state.lastCheckedAt = Date.now();
        state.statusRequestId = null;
      })
      .addCase(checkSubscription.rejected, (state, action) => {
        if (state.statusRequestId && state.statusRequestId !== action.meta.requestId) {
          return;
        }
        state.statusChecking = false;
        state.statusError = action.payload;
        state.statusRequestId = null;
      })
      // Free subscription
      .addCase(subscribeFree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(subscribeFree.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(subscribeFree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(upgradeSubscribeFree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upgradeSubscribeFree.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(upgradeSubscribeFree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(subscribeModulesFree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(subscribeModulesFree.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(subscribeModulesFree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Checkout session
      .addCase(createCheckoutSession.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.checkoutSession = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.checkoutSession = action.payload;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      })
      .addCase(createUpgradeCheckoutSession.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.checkoutSession = null;
      })
      .addCase(createUpgradeCheckoutSession.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.checkoutSession = action.payload;
      })
      .addCase(createUpgradeCheckoutSession.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      })
      .addCase(createModuleCheckoutSession.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.checkoutSession = null;
      })
      .addCase(createModuleCheckoutSession.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.checkoutSession = action.payload;
      })
      .addCase(createModuleCheckoutSession.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      })
      // Verify payment
      .addCase(verifyPaymentStatus.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.paymentStatus = null;
      })
      .addCase(verifyPaymentStatus.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentStatus = action.payload;
      })
      .addCase(verifyPaymentStatus.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
        state.paymentStatus = {
          status: "error",
          error: action.payload,
        };
      })
      .addCase(verifyUpgradePaymentStatus.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.paymentStatus = null;
      })
      .addCase(verifyUpgradePaymentStatus.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentStatus = action.payload;
      })
      .addCase(verifyUpgradePaymentStatus.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
        state.paymentStatus = {
          status: "error",
          error: action.payload,
        };
      })
      .addCase(verifyModulePaymentStatus.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.paymentStatus = null;
      })
      .addCase(verifyModulePaymentStatus.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentStatus = action.payload;
      })
      .addCase(verifyModulePaymentStatus.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
        state.paymentStatus = {
          status: "error",
          error: action.payload,
        };
      })
      // Bank transfer
      .addCase(bankTransferSubscribe.pending, (state) => {
        state.bankTransferLoading = true;
        state.bankTransferError = null;
        state.bankTransferData = null;
      })
      .addCase(bankTransferSubscribe.fulfilled, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferData = action.payload;
      })
      .addCase(bankTransferSubscribe.rejected, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferError = action.payload;
      })
      .addCase(upgradeBankTransferSubscribe.pending, (state) => {
        state.bankTransferLoading = true;
        state.bankTransferError = null;
        state.bankTransferData = null;
      })
      .addCase(upgradeBankTransferSubscribe.fulfilled, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferData = action.payload;
      })
      .addCase(upgradeBankTransferSubscribe.rejected, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferError = action.payload;
      })
      .addCase(moduleBankTransferSubscribe.pending, (state) => {
        state.bankTransferLoading = true;
        state.bankTransferError = null;
        state.bankTransferData = null;
      })
      .addCase(moduleBankTransferSubscribe.fulfilled, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferData = action.payload;
      })
      .addCase(moduleBankTransferSubscribe.rejected, (state, action) => {
        state.bankTransferLoading = false;
        state.bankTransferError = action.payload;
      })
      // VAT step
      .addCase(submitVatStep.pending, (state) => {
        state.vatSubmitting = true;
        state.vatError = null;
      })
      .addCase(submitVatStep.fulfilled, (state, action) => {
        state.vatSubmitting = false;
        state.vatError = null;
      })
      .addCase(submitVatStep.rejected, (state, action) => {
        state.vatSubmitting = false;
        state.vatError = action.payload;
      })
      // Declaration
      .addCase(submitDeclarationStep.pending, (state) => {
        state.declarationSubmitting = true;
        state.declarationError = null;
      })
      .addCase(submitDeclarationStep.fulfilled, (state) => {
        state.declarationSubmitting = false;
        state.declarationError = null;
      })
      .addCase(submitDeclarationStep.rejected, (state, action) => {
        state.declarationSubmitting = false;
        state.declarationError = action.payload;
      });
  },
});

export default subscriptionSlice.reducer;
